/**
 * Shared 2-column checkout layout used by both /plans (Pricing.tsx) and onboarding paywall.
 * Plan selector on the left, order summary glass card on the right.
 */
import { Box, Grid, HStack, Skeleton, VStack } from '@chakra-ui/react';
import { keyframes } from '@chakra-ui/system';
import { useEffect, useMemo, useState } from 'react';

import BigTokenTooltip from 'components/BigTokenTooltip';
import { PurchasingPowerBanner } from 'components/Billing/PurchasingPowerBanner';
import Button from 'components/Button';
import Icon from 'components/Icon';
import Text from 'components/Text';
import { useMediaQueries } from 'layout/MediaQueriesProvider';
import { Plan_Type_Enum } from 'types/databaseEnums';
import { GetPriceResponse } from 'types/edgeFunctions';

// ─── Animations ──────────────────────────────────────────────

const subtleFloat = keyframes`
  0%, 100% { transform: translate(-50%, -50%) scale(1); opacity: 0.5; }
  50% { transform: translate(-50%, -50%) scale(1.05); opacity: 0.8; }
`;

// ─── Types & Config ─────────────────────────────────────────

interface PlanAccent {
  color: string;
  softBg: string;
  border: string;
  glow: string;
  gradient: string;
}

const PLAN_ACCENTS: Record<string, PlanAccent> = {
  [Plan_Type_Enum.Monthly]: {
    color: '#ED8936',
    softBg: 'rgba(237, 137, 54, 0.05)',
    border: 'rgba(237, 137, 54, 0.2)',
    glow: 'rgba(237, 137, 54, 0.12)',
    gradient: 'linear-gradient(135deg, rgba(237, 137, 54, 0.1) 0%, rgba(237, 137, 54, 0.02) 100%)',
  },
  [Plan_Type_Enum.Yearly]: {
    color: '#B794F4',
    softBg: 'rgba(159, 122, 234, 0.05)',
    border: 'rgba(159, 122, 234, 0.2)',
    glow: 'rgba(159, 122, 234, 0.15)',
    gradient: 'linear-gradient(135deg, rgba(159, 122, 234, 0.1) 0%, rgba(159, 122, 234, 0.02) 100%)',
  },
  [Plan_Type_Enum.Lifetime]: {
    color: '#4FD1C5',
    softBg: 'rgba(79, 209, 197, 0.05)',
    border: 'rgba(79, 209, 197, 0.2)',
    glow: 'rgba(79, 209, 197, 0.12)',
    gradient: 'linear-gradient(135deg, rgba(79, 209, 197, 0.1) 0%, rgba(79, 209, 197, 0.02) 100%)',
  },
};

const PLAN_ICONS = {
  [Plan_Type_Enum.Monthly]: 'MonthlyPlan' as const,
  [Plan_Type_Enum.Yearly]: 'YearlyPlan' as const,
  [Plan_Type_Enum.Lifetime]: 'LifetimePlan' as const,
};

const BIG_TOKENS_MAP: Record<string, number> = {
  [Plan_Type_Enum.Monthly]: 5,
  [Plan_Type_Enum.Yearly]: 10,
  [Plan_Type_Enum.Lifetime]: 15,
};

const PLAN_HIGHLIGHTS: Record<string, { label: string; badge?: string }> = {
  [Plan_Type_Enum.Monthly]: { label: 'Flexible monthly billing' },
  [Plan_Type_Enum.Yearly]: { label: 'Best value for learners', badge: 'Save 33%' },
  [Plan_Type_Enum.Lifetime]: { label: 'One payment, forever access', badge: 'Best deal' },
};

const PLAN_DIFFERENTIATORS: Record<string, string[]> = {
  [Plan_Type_Enum.Monthly]: ['5 BigTokens/mo', 'Monthly Supporter Badge'],
  [Plan_Type_Enum.Yearly]: ['10 BigTokens/mo', 'Yearly Daredevils Discord channel', 'Yearly Daredevil Badge'],
  [Plan_Type_Enum.Lifetime]: ['15 BigTokens/mo', 'Lifetime Legends Discord channel', 'Lifetime Legend Badge'],
};

const SOCIAL_PROOF_DEVS = [
  { name: 'kim-fransson', avatar: '/images/kim-fransson.png', profileUrl: 'https://app.bigdevsoon.me/profile/kim-fransson' },
  { name: 'AsadullahSamo', avatar: '/images/asadullah-samo.png', profileUrl: 'https://app.bigdevsoon.me/profile/AsadullahSamo' },
  { name: 'yungProg', avatar: '/images/yung-prog.png', profileUrl: 'https://app.bigdevsoon.me/profile/yungProg' },
];

// ─── Helpers ─────────────────────────────────────────────────

const getTrialEndDate = (): string => {
  const date = new Date();
  date.setDate(date.getDate() + 7);
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

// ─── SVG sub-components ──────────────────────────────────────

const CheckSvg = ({ color, size = 14 }: { color: string; size?: number }) => (
  <Box as="svg" w={`${size}px`} h={`${size}px`} flexShrink={0} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2.5">
    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
  </Box>
);

const LockSvg = () => (
  <Box as="svg" w="12px" h="12px" viewBox="0 0 24 24" fill="none" stroke="rgba(255,255,255,0.3)" strokeWidth="2" flexShrink={0}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z" />
  </Box>
);

const ShieldSvg = () => (
  <Box as="svg" w="12px" h="12px" viewBox="0 0 24 24" fill="none" stroke="rgba(255,255,255,0.3)" strokeWidth="2" flexShrink={0}>
    <path strokeLinecap="round" strokeLinejoin="round" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
  </Box>
);

// ─── PlanCard ────────────────────────────────────────────────

interface PlanCardProps {
  plan: Plan_Type_Enum;
  isSelected: boolean;
  onSelect: () => void;
  isDisabled: boolean;
  price: string;
  periodLabel: string;
  accent: PlanAccent;
  highlight: { label: string; badge?: string };
}

const PlanCard = ({
  plan, isSelected, onSelect, isDisabled, price, periodLabel,
  accent, highlight,
}: PlanCardProps) => {
  const isLifetime = plan === Plan_Type_Enum.Lifetime;
  const tokens = BIG_TOKENS_MAP[plan];

  return (
    <Box
      as="button"
      type="button"
      onClick={onSelect}
      disabled={isDisabled}
      width="100%"
      textAlign="left"
      px={4}
      py={3.5}
      borderRadius="12px"
      bg={isSelected ? accent.softBg : 'transparent'}
      border="1.5px solid"
      borderColor={isSelected ? accent.border : 'whiteAlpha.100'}
      cursor={isDisabled ? 'not-allowed' : 'pointer'}
      opacity={isDisabled ? 0.4 : 1}
      transition="all 0.2s ease"
      _hover={isDisabled ? {} : { borderColor: isSelected ? accent.border : 'whiteAlpha.200', bg: isSelected ? accent.softBg : 'whiteAlpha.50' }}
    >
      <HStack justify="space-between" align="center">
        <HStack spacing={3}>
          <Box w="18px" h="18px" borderRadius="full" border="2px solid" borderColor={isSelected ? accent.color : 'whiteAlpha.300'} display="flex" alignItems="center" justifyContent="center" flexShrink={0} transition="all 0.2s ease">
            {isSelected && <Box w="8px" h="8px" borderRadius="full" bg={accent.color} />}
          </Box>
          <VStack spacing={0} alignItems="flex-start">
            <HStack spacing={2}>
              <Icon type={PLAN_ICONS[plan]} size={18} />
              <Box style={{ fontSize: '15px', fontWeight: 600, color: isSelected ? 'white' : 'rgba(255,255,255,0.8)' }}>
                {plan}
              </Box>
              {highlight.badge && (
                <Box px={1.5} py={0} borderRadius="4px" style={{ fontSize: '10px', fontWeight: 700, textTransform: 'uppercase', letterSpacing: '0.04em', color: accent.color, background: `${accent.color}15`, border: `1px solid ${accent.color}30` }}>
                  {highlight.badge}
                </Box>
              )}
            </HStack>
            <HStack spacing={1}>
              <Box style={{ fontSize: '12px', color: 'rgba(255,255,255,0.4)' }}>{highlight.label}</Box>
            </HStack>
          </VStack>
        </HStack>
        <VStack spacing={0} alignItems="flex-end">
          <HStack spacing={1.5} align="baseline">
            <Box style={{ fontSize: '20px', fontWeight: 700, color: isSelected ? 'white' : 'rgba(255,255,255,0.7)', letterSpacing: '-0.02em' }}>{price}</Box>
            <Box style={{ fontSize: '12px', fontWeight: 500, color: 'rgba(255,255,255,0.35)' }}>{periodLabel}</Box>
          </HStack>
          <HStack spacing={1}>
            <Icon type="Bigtokens" size={12} />
            <Box style={{ fontSize: '11px', color: 'rgba(255,255,255,0.4)' }}>{tokens} BigTokens/mo</Box>
          </HStack>
        </VStack>
      </HStack>
    </Box>
  );
};

const PlanCardSkeleton = () => (
  <Box px={4} py={3.5} borderRadius="12px" border="1.5px solid" borderColor="whiteAlpha.100">
    <HStack justify="space-between">
      <HStack spacing={3}>
        <Skeleton w="18px" h="18px" borderRadius="full" />
        <VStack spacing={1} alignItems="flex-start">
          <Skeleton h="15px" w="80px" borderRadius="sm" />
          <Skeleton h="12px" w="120px" borderRadius="sm" />
        </VStack>
      </HStack>
      <Skeleton h="20px" w="50px" borderRadius="sm" />
    </HStack>
  </Box>
);

// ─── Features Section ────────────────────────────────────────

const FeaturesSection = ({ planDiffs, accentColor, planName, isMobile }: { planDiffs: string[]; accentColor: string; planName: string; isMobile: boolean }) => (
  <Box mt={isMobile ? 6 : 2}>
    <Box style={{ fontSize: '11px', fontWeight: 600, color: 'rgba(255,255,255,0.35)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '12px' }}>
      {planName} plan includes
    </Box>
    <VStack spacing={2} alignItems="flex-start">
      {planDiffs.map((feature) => {
        const isBigTokens = feature.toLowerCase().includes('bigtokens');
        return (
          <HStack key={feature} spacing={2.5}>
            <CheckSvg color={accentColor} />
            <HStack spacing={0.5} align="center">
              <Box style={{ fontSize: '13px', fontWeight: 500, color: 'rgba(255,255,255,0.7)' }}>{feature}</Box>
              {isBigTokens && <BigTokenTooltip />}
            </HStack>
          </HStack>
        );
      })}
      {planDiffs.length < 3 && (
        <HStack spacing={2.5} visibility="hidden" aria-hidden="true">
          <CheckSvg color={accentColor} />
          <Box style={{ fontSize: '13px' }}>&nbsp;</Box>
        </HStack>
      )}
    </VStack>
  </Box>
);

// ─── Main Component ──────────────────────────────────────────

export interface PlanCheckoutProps {
  priceData: GetPriceResponse;
  isPriceLoading: boolean;
  isTrialEligible: boolean;
  onSubscribe: (plan: Plan_Type_Enum) => Promise<void>;
  processingPlan: Plan_Type_Enum | null;
  availablePlans?: Plan_Type_Enum[];
  defaultPlan?: Plan_Type_Enum;
  showContactMessage?: boolean;
  isPPPActive: boolean;
  setIsPPPActive: (v: boolean) => void;
}

const PlanCheckout = ({
  priceData,
  isPriceLoading,
  isTrialEligible,
  onSubscribe,
  processingPlan,
  availablePlans,
  defaultPlan,
  showContactMessage,
  isPPPActive,
  setIsPPPActive,
}: PlanCheckoutProps) => {
  const { isSmallScreen } = useMediaQueries();

  const isLifetimeSoldOut = process.env.REACT_APP_LIFETIME_SOLD_OUT === 'true';
  const discount = priceData?.discount || 0;
  const showPPP = discount > 0;

  const tabs = availablePlans || [Plan_Type_Enum.Monthly, Plan_Type_Enum.Yearly, Plan_Type_Enum.Lifetime];

  const [selectedPlan, setSelectedPlan] = useState<Plan_Type_Enum>(() => {
    if (defaultPlan) return defaultPlan;
    if (tabs.length === 1) return tabs[0];
    return Plan_Type_Enum.Monthly;
  });

  useEffect(() => {
    if (isLifetimeSoldOut && selectedPlan === Plan_Type_Enum.Lifetime) {
      setSelectedPlan(Plan_Type_Enum.Yearly);
    }
  }, [isLifetimeSoldOut, selectedPlan]);

  const planInfo = useMemo(() => {
    if (!priceData?.plans?.[selectedPlan]) return null;
    const p = priceData.plans[selectedPlan];
    const base = p.base_price ?? 0;
    const disc = p.discounted_price ?? 0;

    if (selectedPlan === Plan_Type_Enum.Yearly) {
      const monthlyEq = isPPPActive
        ? Math.round((disc / 12) * 100) / 100
        : Math.round((base / 12) * 100) / 100;
      return { price: disc, basePrice: base, periodLabel: '/yr', monthlyEquivalent: `$${monthlyEq}/mo`, bigTokens: BIG_TOKENS_MAP[selectedPlan] };
    }

    return {
      price: disc,
      basePrice: base,
      periodLabel: selectedPlan === Plan_Type_Enum.Lifetime ? ' once' : '/mo',
      monthlyEquivalent: null,
      bigTokens: BIG_TOKENS_MAP[selectedPlan],
    };
  }, [priceData, selectedPlan, isPPPActive]);

  const getPlanPrice = (plan: Plan_Type_Enum): string => {
    if (!priceData?.plans?.[plan]) return '...';
    const p = priceData.plans[plan];
    return `$${isPPPActive ? (p.discounted_price ?? 0) : (p.base_price ?? 0)}`;
  };

  const getPlanPeriod = (plan: Plan_Type_Enum): string => {
    if (plan === Plan_Type_Enum.Yearly) return '/yr';
    if (plan === Plan_Type_Enum.Lifetime) return '';
    return '/mo';
  };

  const isLifetime = selectedPlan === Plan_Type_Enum.Lifetime;
  const hasTrial = !isLifetime && isTrialEligible;
  const accent = PLAN_ACCENTS[selectedPlan];
  const showDiscount = isPPPActive && planInfo && planInfo.price !== planInfo.basePrice;
  const displayPrice = planInfo ? (isPPPActive ? planInfo.price : planInfo.basePrice) : 0;
  const planDiffs = PLAN_DIFFERENTIATORS[selectedPlan] || [];

  const ctaText = useMemo(() => {
    if (isLifetime) return 'Get Lifetime Access';
    if (isTrialEligible) return 'Start 7-Day Free Trial';
    return `Get ${selectedPlan} Plan`;
  }, [selectedPlan, isTrialEligible, isLifetime]);

  const hasPlanSelector = tabs.length > 1;

  return (
    <Box width="100%" maxW="920px" mx="auto">
      <Grid
        templateColumns={isSmallScreen ? '1fr' : '1.2fr 1fr'}
        gap={isSmallScreen ? 5 : 8}
        alignItems="start"
      >
        {/* ─── LEFT COLUMN: Plan Selection + Features ─── */}
        <VStack spacing={3} alignItems="stretch">
          {hasPlanSelector && (
            <Box style={{ fontSize: '11px', fontWeight: 600, color: 'rgba(255,255,255,0.35)', textTransform: 'uppercase', letterSpacing: '0.08em' }}>
              Choose your plan
            </Box>
          )}

          {isPriceLoading ? (
            <>
              <PlanCardSkeleton />
              {hasPlanSelector && <PlanCardSkeleton />}
              {hasPlanSelector && <PlanCardSkeleton />}
            </>
          ) : (
            tabs.map((plan) => (
              <PlanCard
                key={plan}
                plan={plan}
                isSelected={selectedPlan === plan}
                onSelect={() => setSelectedPlan(plan)}
                isDisabled={plan === Plan_Type_Enum.Lifetime && isLifetimeSoldOut}
                price={getPlanPrice(plan)}
                periodLabel={getPlanPeriod(plan)}
                accent={PLAN_ACCENTS[plan]}
                highlight={PLAN_HIGHLIGHTS[plan]}
              />
            ))
          )}

          {/* Contact message for terminated users with locked plan */}
          {showContactMessage && (
            <Box
              mt={2}
              p={4}
              borderRadius="lg"
              bg="whiteAlpha.50"
              border="1px solid"
              borderColor="whiteAlpha.100"
            >
              <Text fontSize="small" opacity="secondary" textAlign="center">
                Want to change your plan? Contact us at{' '}
                <Box
                  as="a"
                  href="mailto:adrian@bigdevsoon.me"
                  style={{ color: '#B794F4', fontWeight: 600 }}
                >
                  adrian@bigdevsoon.me
                </Box>
              </Text>
            </Box>
          )}

          {/* Features — desktop only */}
          {!isSmallScreen && (
            <FeaturesSection
              planDiffs={planDiffs}
              planName={selectedPlan}
              accentColor={accent.color}
              isMobile={false}
            />
          )}
        </VStack>

        {/* ─── RIGHT COLUMN: Order Summary + CTA ─── */}
        <Box position={isSmallScreen ? 'relative' : 'sticky'} top={isSmallScreen ? undefined : '24px'}>
          {/* Ambient glow */}
          <Box
            position="absolute"
            top="40%"
            left="50%"
            w="70%"
            h="50%"
            borderRadius="50%"
            bg={accent.glow}
            filter="blur(50px)"
            animation={`${subtleFloat} 5s ease-in-out infinite`}
            pointerEvents="none"
            zIndex={0}
          />

          {/* Glass card */}
          <VStack
            spacing={0}
            width="100%"
            minH={isSmallScreen ? undefined : '460px'}
            borderRadius="20px"
            overflow="hidden"
            bg="rgba(255, 255, 255, 0.03)"
            border="1px solid"
            borderColor="whiteAlpha.100"
            position="relative"
            zIndex={1}
            style={{
              backdropFilter: 'blur(12px)',
              WebkitBackdropFilter: 'blur(12px)',
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.3), inset 0 1px 0 rgba(255, 255, 255, 0.04)',
            }}
          >
            {/* Summary header */}
            <Box width="100%" px={6} pt={5} pb={4} style={{ background: accent.gradient }}>
              <Box style={{ fontSize: '11px', fontWeight: 600, color: 'rgba(255,255,255,0.4)', textTransform: 'uppercase', letterSpacing: '0.08em', marginBottom: '16px' }}>
                Order summary
              </Box>

              <HStack spacing={2} mb={3}>
                <Icon type={PLAN_ICONS[selectedPlan]} size={20} />
                <Box style={{ fontSize: '16px', fontWeight: 600, color: 'white' }}>{selectedPlan} Plan</Box>
              </HStack>

              {/* Price */}
              {isPriceLoading || !planInfo ? (
                <Skeleton h="40px" w="100px" borderRadius="md" />
              ) : (
                <HStack spacing={2} align="baseline" flexWrap="wrap">
                  {showDiscount && (
                    <Box position="relative" display="inline-flex">
                      <Box style={{ fontSize: '16px', fontWeight: 500, color: 'rgba(255,255,255,0.25)' }}>
                        ${planInfo.basePrice}
                      </Box>
                      <Box position="absolute" height="1.5px" width="100%" bg="whiteAlpha.400" top="50%" left="0" transform="translateY(-50%) rotate(-8deg)" />
                    </Box>
                  )}
                  <Box style={{ fontSize: '36px', fontWeight: 700, lineHeight: 1, color: 'white', letterSpacing: '-0.03em' }}>
                    ${selectedPlan === Plan_Type_Enum.Yearly ? Math.round(displayPrice * 100) / 100 : displayPrice}
                  </Box>
                  <Box style={{ fontSize: '14px', fontWeight: 500, color: 'rgba(255,255,255,0.35)' }}>
                    {planInfo.periodLabel}
                  </Box>
                </HStack>
              )}
            </Box>

            {/* Line items */}
            <VStack spacing={0} width="100%" px={6}>
              <Box w="100%" h="1px" bg="whiteAlpha.100" />
              <HStack justify="space-between" width="100%" py={3}>
                <Box style={{ fontSize: '15px', fontWeight: 600, color: 'rgba(255,255,255,0.9)' }}>
                  Due today
                </Box>
                <Box style={{ fontSize: hasTrial ? '18px' : '14px', fontWeight: hasTrial ? 700 : 600, color: hasTrial ? '#68D391' : 'white', letterSpacing: hasTrial ? '-0.01em' : undefined }}>
                  {hasTrial ? '$0.00' : `$${displayPrice}`}
                </Box>
              </HStack>

              {hasTrial ? (
                <>
                  <Box w="100%" h="1px" bg="whiteAlpha.50" />
                  <HStack justify="space-between" width="100%" py={3}>
                    <Box style={{ fontSize: '13px', color: 'rgba(255,255,255,0.4)' }}>After trial ({getTrialEndDate()})</Box>
                    <HStack spacing={2} align="baseline">
                      <Box style={{ fontSize: '13px', fontWeight: 500, color: 'rgba(255,255,255,0.6)' }}>
                        ${displayPrice}{planInfo?.periodLabel}
                      </Box>
                      {selectedPlan === Plan_Type_Enum.Yearly && planInfo?.monthlyEquivalent && (
                        <Box style={{ fontSize: '11px', fontWeight: 600, color: '#B794F4' }}>
                          ({planInfo.monthlyEquivalent})
                        </Box>
                      )}
                    </HStack>
                  </HStack>
                </>
              ) : (
                <>
                  <Box w="100%" h="1px" bg="whiteAlpha.50" />
                  <HStack justify="space-between" width="100%" py={3}>
                    <Box style={{ fontSize: '13px', color: 'rgba(255,255,255,0.4)' }}>
                      {isLifetime ? 'Pay once, yours forever' : selectedPlan === Plan_Type_Enum.Yearly ? 'Billed yearly' : 'Billed monthly'}
                    </Box>
                    {isLifetime && (
                      <Box style={{ fontSize: '13px', color: 'rgba(255,255,255,0.4)' }}>
                        One-time payment
                      </Box>
                    )}
                  </HStack>
                </>
              )}

              <Box w="100%" h="1px" bg="whiteAlpha.50" />
            </VStack>

            {/* CTA + trust */}
            <VStack spacing={3} width="100%" px={6} pb={5} pt={4} mt="auto">
              {showPPP && (
                <PurchasingPowerBanner
                  discount={discount}
                  isPPPActive={isPPPActive}
                  setIsPPPActive={setIsPPPActive}
                  countryFlag={priceData?.country_flag}
                  countryName={priceData?.country_name}
                />
              )}

              <Button
                width="100%"
                height="52px"
                fontSize="medium"
                fontWeight="semibold"
                borderRadius="12px"
                scheme="gradient"
                isLoading={isPriceLoading || processingPlan !== null}
                isDisabled={isPriceLoading}
                onClick={() => onSubscribe(selectedPlan)}
              >
                {ctaText}
              </Button>

              {!showPPP && (
                <Box style={{ fontSize: '12px', color: 'rgba(255,255,255,0.4)', textAlign: 'center' }}>
                  Have a promo code? Enter it at checkout.
                </Box>
              )}

              {/* Trust row */}
              <HStack spacing={3} justify="center" mt={1} flexWrap="wrap">
                <HStack spacing={1.5}>
                  <LockSvg />
                  <Box style={{ fontSize: '11px', color: 'rgba(255,255,255,0.25)' }}>Secured by Stripe</Box>
                </HStack>
                <HStack spacing={1.5}>
                  {isLifetime ? (
                    <CheckSvg color="rgba(255,255,255,0.3)" size={12} />
                  ) : (
                    <Box as="svg" w="12px" h="12px" viewBox="0 0 24 24" fill="none" stroke="rgba(255,255,255,0.3)" strokeWidth="2" flexShrink={0}>
                      <path strokeLinecap="round" strokeLinejoin="round" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                    </Box>
                  )}
                  <Box style={{ fontSize: '11px', color: 'rgba(255,255,255,0.25)' }}>
                    {isLifetime ? 'All future updates' : 'Cancel anytime'}
                  </Box>
                </HStack>
                <HStack spacing={1.5}>
                  <ShieldSvg />
                  <Box style={{ fontSize: '11px', color: 'rgba(255,255,255,0.25)' }}>14-day money back</Box>
                </HStack>
              </HStack>
            </VStack>
          </VStack>

          {/* Social proof */}
          <HStack mt={4} spacing={3} justify="center" align="center">
            <Box display="flex">
              {SOCIAL_PROOF_DEVS.map((dev, i) => (
                <Box
                  key={dev.name}
                  as="a"
                  href={dev.profileUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  w="32px"
                  h="32px"
                  borderRadius="full"
                  overflow="hidden"
                  border="2px solid"
                  borderColor="gray.800"
                  flexShrink={0}
                  transition="all 0.2s ease"
                  _hover={{ transform: 'scale(1.1)', zIndex: 10 }}
                  position="relative"
                  zIndex={SOCIAL_PROOF_DEVS.length - i}
                  ml={i === 0 ? '0' : '-10px'}
                >
                  <Box as="img" src={dev.avatar} alt={dev.name} w="100%" h="100%" objectFit="cover" loading="lazy" />
                </Box>
              ))}
            </Box>
            <Box style={{ fontSize: '14px', fontWeight: 500, color: 'rgba(255,255,255,0.6)' }}>
              Trusted by{' '}
              <Box as="span" style={{ fontWeight: 700, color: 'rgba(255,255,255,0.9)' }}>11,500+</Box>{' '}
              developers
            </Box>
          </HStack>
        </Box>
      </Grid>

      {/* Features on mobile */}
      {isSmallScreen && (
        <FeaturesSection
          planDiffs={planDiffs}
          planName={selectedPlan}
          accentColor={accent.color}
          isMobile
        />
      )}
    </Box>
  );
};

export default PlanCheckout;
